// Month-by-month Rego/COF distribution + renewal suggestions, mirroring
// the by-hand spreadsheet Janelle used to run to avoid a huge outgoing in
// any one month (and specifically to keep December/January light). See
// src/lib/fleetSpacing.ts for the underlying logic and settings.
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/dates";
import {
  loadFleetSpacingSettings,
  computeMonthDistribution,
  suggestRegoRenewal,
  suggestCofRenewal,
  type FleetSpacingSettings,
  type MonthBucket,
} from "@/lib/fleetSpacing";

function DistributionTable({ title, buckets }: { title: string; buckets: MonthBucket[] }) {
  const allowedBuckets = buckets.filter((b) => b.allowed);
  const total = allowedBuckets.reduce((sum, b) => sum + b.count, 0);
  const ideal = allowedBuckets.length > 0 ? total / allowedBuckets.length : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h4 className="font-medium text-foreground">{title}</h4>
        <div className="text-right">
          <div className="text-lg font-semibold text-yellow-500">{ideal.toFixed(1)} / month</div>
          <div className="text-xs text-muted-foreground">ideal to stay even ({total} across {allowedBuckets.length} allowed months)</div>
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto rounded-md border border-border">
        <Table>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead className="text-right">Count</TableHead>
              <TableHead className="text-right">vs Ideal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buckets.map((b) => {
              const diff = b.count - ideal;
              return (
                <TableRow key={`${b.year}-${b.monthIndex0}`} className={!b.allowed ? "opacity-40" : ""}>
                  <TableCell className="py-1">
                    {b.label}
                    {!b.allowed && <span className="text-xs text-muted-foreground ml-1">(excluded)</span>}
                  </TableCell>
                  <TableCell
                    className={`py-1 text-right font-medium ${
                      b.allowed && diff > 0.5 ? "text-red-400" : b.allowed && diff < -0.5 ? "text-green-500" : ""
                    }`}
                  >
                    {b.count}
                  </TableCell>
                  <TableCell className="py-1 text-right text-xs text-muted-foreground">
                    {b.allowed ? (diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1)) : "--"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function FleetSpacingReport({
  open,
  onOpenChange,
  vehicles,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  vehicles: any[];
}) {
  const [settings, setSettings] = useState<FleetSpacingSettings | null>(null);

  useEffect(() => {
    if (!open) return;
    loadFleetSpacingSettings().then(setSettings);
  }, [open]);

  const regoBuckets = useMemo(
    () => (settings ? computeMonthDistribution(vehicles, "rego_date", settings.regoAllowedMonths, 26) : []),
    [settings, vehicles],
  );
  const cofBuckets = useMemo(
    () => (settings ? computeMonthDistribution(vehicles, "cof_date", settings.cofAllowedMonths, 26) : []),
    [settings, vehicles],
  );

  // Vehicles due for Rego/COF within the next 60 days get a live renewal
  // suggestion -- further out isn't actionable yet, and would just churn
  // as other vehicles' suggestions shift the distribution around it.
  const dueSoonSuggestions = useMemo(() => {
    if (!settings) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + 60);
    const rows: { vehicle_number: string; type: "Rego" | "COF"; current: string; suggestion: ReturnType<typeof suggestRegoRenewal> }[] = [];

    for (const v of vehicles) {
      if (v.status && v.status !== "active") continue;
      if (v.is_contractor_vehicle) continue;

      if (v.rego_date && new Date(v.rego_date) <= cutoff) {
        rows.push({
          vehicle_number: v.vehicle_number,
          type: "Rego",
          current: v.rego_date,
          suggestion: suggestRegoRenewal(v.rego_date, vehicles, settings.regoAllowedMonths, v.vehicle_number),
        });
      }
      if (v.cof_date && new Date(v.cof_date) <= cutoff) {
        const intervalMonths = v.cof_interval_months || settings.cofDefaultIntervalMonths;
        rows.push({
          vehicle_number: v.vehicle_number,
          type: "COF",
          current: v.cof_date,
          suggestion: suggestCofRenewal(v.cof_date, intervalMonths, vehicles, settings.cofAllowedMonths, settings.cofFlexMonths, v.vehicle_number),
        });
      }
    }

    return rows.sort((a, b) => new Date(a.current).getTime() - new Date(b.current).getTime());
  }, [settings, vehicles]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fleet Spacing</DialogTitle>
          <p className="text-sm text-muted-foreground">
            How Rego/COF renewals are spread across the months you've allowed (Settings → Fleet Defaults → Rego / COF), so
            outgoings don't cluster in any one month.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
          <DistributionTable title="Registration (Rego)" buckets={regoBuckets} />
          <DistributionTable title="Certificate of Fitness (COF/WOF)" buckets={cofBuckets} />
        </div>

        <div className="mt-6 space-y-2">
          <h4 className="font-medium text-foreground">Due Within 60 Days -- Suggested Renewals</h4>
          {dueSoonSuggestions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing due within 60 days.</p>
          ) : (
            <div className="rounded-md border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Current Due</TableHead>
                    <TableHead>Suggested</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dueSoonSuggestions.map((row, i) => (
                    <TableRow key={`${row.vehicle_number}-${row.type}-${i}`}>
                      <TableCell className="font-medium">{row.vehicle_number}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{row.type}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(row.current)}</TableCell>
                      <TableCell>
                        {row.suggestion ? (
                          <span>
                            {row.type === "Rego" ? `${row.suggestion.months} months → ` : ""}
                            {formatDate(row.suggestion.targetDate)} ({row.suggestion.targetLabel}, {row.suggestion.targetCount} others)
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No allowed month found</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
